import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { lastValueFrom } from 'rxjs';
import {
  BdfService,
  BdfJob,
  BdfStatusWsEvent,
  BdfGenerateResult,
  BdfGenerateOptions,
} from '@infinitform/platform-core';
import { CLOUD_PLATFORM_CONFIG } from '../config';

@Injectable()
export class CloudBdfService implements BdfService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(CLOUD_PLATFORM_CONFIG);

  private readonly statusMap = signal<Map<string, BdfJob>>(new Map());

  // Monotonic ordering so a late HTTP "queued" response can't overwrite a "running"
  // frame that raced in over the websocket. done/error are terminal (highest).
  private static readonly STATUS_RANK: Record<BdfJob['status'], number> = {
    idle: 0,
    queued: 1,
    running: 2,
    done: 3,
    error: 3,
  };

  handleWsEvent(event: BdfStatusWsEvent): void {
    if (event.type !== 'bdf:status') return;
    this.setStatus(event.projectId, event.bdfJob);
  }

  bdfStatusFor(projectId: string): Signal<BdfJob | null> {
    return computed(() => this.statusMap().get(projectId) ?? null);
  }

  async generate(
    projectId: string,
    childIds?: string[],
    options?: BdfGenerateOptions,
  ): Promise<BdfGenerateResult> {
    const highRes = !!options?.highRes;
    const body: { childIds?: string[]; highRes?: boolean } = {};
    if (childIds && childIds.length > 0) body.childIds = childIds;
    if (highRes) body.highRes = true;

    // Optimistically show "queued" the instant the user clicks. The BDF job now runs on
    // Fargate (a ~1-3 min cold start before the container posts its first progress), so
    // without this the UI would look idle until then. We roll this back if the request
    // is rejected, and the authoritative websocket frames take over once they arrive.
    const targetIds = childIds && childIds.length > 0 ? childIds : [projectId];
    const previous = new Map<string, BdfJob | null>(
      targetIds.map((id) => [id, this.statusMap().get(id) ?? null]),
    );
    targetIds.forEach((id) => this.setStatus(id, { status: 'queued', highRes }));

    try {
      const result = await lastValueFrom(
        this.http.post<BdfGenerateResult>(
          `${this.config.apiBaseUrl}/project/${projectId}/generate-bdf`,
          body,
        ),
      );
      this.reconcileResult(projectId, result);
      return result;
    } catch (err) {
      // Roll back only where our optimistic "queued" is still showing — if an
      // authoritative websocket frame (running/error/done) already arrived, leave it.
      previous.forEach((job, id) => {
        if (this.statusMap().get(id)?.status !== 'queued') return;
        job ? this.setStatus(id, job) : this.removeStatus(id);
      });
      throw err;
    }
  }

  /** Apply the HTTP response's job(s) without ever downgrading a more-advanced state. */
  private reconcileResult(projectId: string, result: BdfGenerateResult): void {
    if ('children' in result) {
      result.children.forEach((c) => this.setStatus(c.id, c.bdfJob, true));
    } else {
      this.setStatus(projectId, result.bdfJob, true);
    }
  }

  private setStatus(id: string, job: BdfJob, noDowngrade = false): void {
    this.statusMap.update((map) => {
      if (noDowngrade) {
        const current = map.get(id);
        if (
          current &&
          CloudBdfService.STATUS_RANK[job.status] < CloudBdfService.STATUS_RANK[current.status]
        ) {
          return map;
        }
      }
      const next = new Map(map);
      next.set(id, job);
      return next;
    });
  }

  private removeStatus(id: string): void {
    this.statusMap.update((map) => {
      if (!map.has(id)) return map;
      const next = new Map(map);
      next.delete(id);
      return next;
    });
  }

  async cancel(projectId: string, childIds?: string[]): Promise<void> {
    const body = childIds && childIds.length > 0 ? { childIds } : {};
    await lastValueFrom(
      this.http.post<{ message: string }>(
        `${this.config.apiBaseUrl}/project/${projectId}/bdf/cancel`,
        body,
      ),
    );
  }

  async getDownloadUrl(projectId: string): Promise<string> {
    const { url } = await lastValueFrom(
      this.http.get<{ url: string }>(
        `${this.config.apiBaseUrl}/project/${projectId}/bdf/download`,
      ),
    );
    return url;
  }

  async getBundleUrl(masterId: string, childIds: string[]): Promise<string> {
    const params = childIds.join(',');
    const presignedUrl = await lastValueFrom(
      this.http.get(
        `${this.config.apiBaseUrl}/project/${masterId}/bdf/download-bundle?children=${params}`,
        { responseType: 'text' },
      ),
    );
    return presignedUrl;
  }
}
